import BaseCheckbox from 'components/atoms/base-checkbox';
import React from 'react';
import BaseInputNumber from 'components/atoms/base-input-number';
import { Radio } from 'antd';
import BaseTextArea from 'components/atoms/base-textarea';
import BaseInput from 'components/atoms/base-input';
import BaseDatePicker from 'components/atoms/datePicker';

import s from './styles.module.scss';
import FormItem from './form-item';
import MaCriticalFormItem from './ma-critical-form-item';

import type { FormikProps } from 'formik';
import type { FieldValueMap, TFieldProps, TFormItem } from '../types';

const resetSubItems = (
  subItems: TFormItem[],
  formik: FormikProps<any>,
  subItemKey: string,
) => {
  subItems?.forEach((subItem) => {
    if (subItem.type === 'info') return;

    if (
      subItem.type === 'input' ||
      subItem.type === 'input-number' ||
      subItem.type === 'input-date' ||
      subItem.type === 'textarea' ||
      subItem.type === 'radio'
    ) {
      formik.setFieldValue(`${subItemKey}${subItem.name}`, undefined);
    }

    if (subItem.type === 'checkbox') {
      formik.setFieldValue(`${subItemKey}${subItem.name}`, []);
    }

    // TFormItem → items
    if (subItem.items?.length) {
      resetSubItems(subItem.items, formik, subItemKey);
    }

    // checkbox / radio → options → subItems
    subItem.options?.forEach((option) => {
      if (option.subItems?.length) {
        resetSubItems(option.subItems, formik, subItemKey);
      }
    });
  });
};

export const fieldTypeComponents: {
  [K in keyof FieldValueMap]: (props: TFieldProps<K>) => React.ReactNode;
} = {
  empty: () => null,
  checkbox: ({
    item,
    onChange,
    value = [],
    formik,
    readingStatus,
    readingType,
    form,
  }) => {
    return item?.options?.map((option) => {
      const checkboxValue = value.includes(option.value);

      if (option?.type === 'info') {
        return (
          <div key={option.key}>
            <p className={s['info-option']}>{option.label}</p>
            {option?.subItems &&
              option?.subItems.map((i) => (
                <MaCriticalFormItem item={i} key={i.key} formik={formik} />
              ))}
          </div>
        );
      }

      return (
        <React.Fragment key={option.key}>
          <BaseCheckbox
            placeholder={option.label}
            value={checkboxValue}
            onChange={(v) => {
              let newValues = value;
              if (v) newValues.push(option.value);
              else newValues = newValues.filter((val) => val !== option.value);

              const newCheckboxValue = newValues.includes(option.value);

              onChange(newValues);
              const subItemKey =
                form === 'normal-risk'
                  ? `${readingStatus}.${readingType}.`
                  : '';

              if (!newCheckboxValue && option.subItems) {
                resetSubItems(option.subItems, formik, subItemKey);
              }
            }}
          />
          {option?.subItems?.map((subItem) => {
            const isMoveRight = [
              'input',
              'input-number',
              'checkbox',
              'radio',
              'textarea',
            ].includes(subItem.type);
            const isCurrentOptionSelected = value.includes(option.value);

            if (
              option?.hideSubitemsIfParentNotSelected &&
              !isCurrentOptionSelected
            ) {
              return null;
            }
            const paddingLeft = isMoveRight ? 20 : 0;

            return (
              <div style={{ paddingLeft }} key={subItem.key}>
                {form === 'normal-risk' ? (
                  <FormItem
                    item={subItem}
                    key={subItem.key}
                    formik={formik}
                    readingStatus={readingStatus}
                    readingType={readingType}
                  />
                ) : (
                  <MaCriticalFormItem
                    item={subItem}
                    key={subItem.key}
                    formik={formik}
                  />
                )}
              </div>
            );
          })}
        </React.Fragment>
      );
    });
  },
  radio: ({
    item,
    onChange,
    value,
    formik,
    readingStatus,
    readingType,
    form,
  }) => (
    <Radio.Group value={value} className={s['radio-items']} key={item.key}>
      {item?.options?.map((option) => {
        if (option?.type === 'info') {
          return (
            <div key={option.key}>
              <p className={s['info-option']}>{option.label}</p>
              {option?.subItems &&
                option?.subItems.map((i) => (
                  <MaCriticalFormItem item={i} key={i.key} formik={formik} />
                ))}
            </div>
          );
        }

        return (
          <React.Fragment key={option.key}>
            <Radio
              value={option.value}
              disabled={option.disabled}
              onChange={(e) => {
                onChange(e.target.value);
                const subItemKey =
                  form === 'normal-risk'
                    ? `${readingStatus}.${readingType}.`
                    : '';
                if (option.subItems) {
                  resetSubItems(option.subItems, formik, subItemKey);
                }
              }}
            >
              <span style={{ cursor: 'pointer' }}>{option.label}</span>
            </Radio>
            {option?.subItems?.map((subItem) => {
              const isMoveRight = [
                'input',
                'input-number',
                'checkbox',
                'radio',
                'textarea',
              ].includes(subItem.type);
              const isCurrentOptionSelected = value === option.value;

              if (
                option?.hideSubitemsIfParentNotSelected &&
                !isCurrentOptionSelected
              ) {
                return null;
              }
              const paddingLeft = isMoveRight ? 20 : 0;

              return (
                <div style={{ paddingLeft }} key={subItem.key}>
                  {form === 'normal-risk' ? (
                    <FormItem
                      item={subItem}
                      key={subItem.key}
                      formik={formik}
                      readingStatus={readingStatus}
                      readingType={readingType}
                    />
                  ) : (
                    <MaCriticalFormItem
                      item={subItem}
                      key={subItem.key}
                      formik={formik}
                    />
                  )}
                </div>
              );
            })}
          </React.Fragment>
        );
      })}
    </Radio.Group>
  ),
  textarea: ({ onChange, value, item }) => (
    <BaseTextArea
      rows={6}
      placeholder="Type here details"
      onChange={(v) => onChange(v)}
      value={value}
      key={item.key}
    />
  ),
  input: ({ onChange, value, item }) => (
    <BaseInput value={value} onChange={onChange} key={item.key} />
  ),
  'input-number': ({ onChange, value, item }) => {
    const validation = item.validation ?? {};
    const { min, max } = validation;
    return (
      <BaseInputNumber
        placeholder="0.0"
        min={min}
        max={max}
        value={value}
        onChange={onChange}
        key={item.key}
      />
    );
  },
  'input-date': ({ onChange, value, item }) => {
    return <BaseDatePicker value={value} onChange={onChange} key={item.key} />;
  },
};
